import { memo, useState, useMemo, useRef } from 'react';
import { Play, Pause, Search, Music, Volume2, Sparkles, Loader2, Check, Square } from 'lucide-react';
import { BGM_PRESETS } from '@/features/media-library/data/bgm-presets';
import { setMediaDragData, clearMediaDragData } from '@/features/media-library/utils/drag-data-cache';
import { useTimelineStore } from '@/features/timeline/stores/timeline-store';
import type { AudioItem, TimelineItem, TimelineTrack } from '@/types/timeline';

export const BgmPanel = memo(function BgmPanel() {
    const [searchQuery, setSearchQuery] = useState('');
    const [playingId, setPlayingId] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const audioRef = useRef<HTMLAudioElement | null>(null);

    const filteredBgms = useMemo(() => {
        return BGM_PRESETS.filter(bgm =>
            bgm.fileName.toLowerCase().includes(searchQuery.toLowerCase())
        );
    }, [searchQuery]);

    const handlePlay = (id: string) => {
        if (playingId === id) {
            audioRef.current?.pause();
            setPlayingId(null);
            return;
        }
        if (audioRef.current) {
            audioRef.current.pause();
        }
        const audio = new Audio(id);
        audio.play();
        audioRef.current = audio;
        setPlayingId(id);
        audio.onended = () => {
            setPlayingId(null);
        };
    };

    const handleDragStart = (e: React.DragEvent, bgm: any) => {
        e.dataTransfer.effectAllowed = 'copy';
        const dragData = {
            type: 'media-item' as const,
            mediaId: bgm.id,
            mediaType: 'audio' as const,
            fileName: bgm.fileName,
            duration: bgm.duration,
        };
        e.dataTransfer.setData('application/json', JSON.stringify(dragData));
        setMediaDragData(dragData);
    };

    const handleRandomAssign = async () => {
        setIsLoading(true);
        try {
            const items = useTimelineStore.getState().items;
            const fps = useTimelineStore.getState().fps;
            const addItem = useTimelineStore.getState().addItem;
            const tracks = useTimelineStore.getState().tracks;

            if (items.length === 0) {
                alert('타임라인에 아이템이 없습니다.');
                setIsLoading(false);
                return;
            }

            // Calculate total duration of the project
            const maxFrame = items.reduce((max: number, item: TimelineItem) => Math.max(max, item.from + item.durationInFrames), 0);
            if (maxFrame === 0) {
                alert('타임라인이 비어있습니다.');
                setIsLoading(false);
                return;
            }

            // Pick a random BGM
            const randomBgm = BGM_PRESETS[Math.floor(Math.random() * BGM_PRESETS.length)];
            if (!randomBgm) return;

            // BGM 트랙 찾기 또는 생성
            let bgmTrack = tracks.find((t: TimelineTrack) => t.name?.toLowerCase().includes('bgm'));
            const maxOrder = tracks.length > 0 ? Math.max(...tracks.map((t: TimelineTrack) => t.order ?? 0)) : 0;

            if (!bgmTrack) {
                bgmTrack = {
                    id: crypto.randomUUID(),
                    name: 'BGM',
                    height: 48,
                    locked: false,
                    visible: true,
                    muted: false,
                    solo: false,
                    order: maxOrder + 1,
                    items: [],
                };
                const updatedTracks = [...tracks, bgmTrack].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
                useTimelineStore.getState().setTracks(updatedTracks);
            } else {
                // 기존 BGM 아이템 제거 및 순서를 맨 아래로 이동
                const bgmItemsToRemove = items
                    .filter((item: TimelineItem) => item.trackId === bgmTrack!.id)
                    .map((item: TimelineItem) => item.id);
                if (bgmItemsToRemove.length > 0) {
                    useTimelineStore.getState().removeItems(bgmItemsToRemove);
                }

                // 만약 BGM 트랙이 맨 아래가 아니라면 순서 조정
                if ((bgmTrack.order ?? 0) < maxOrder) {
                    const updatedTracks = tracks.map(t =>
                        t.id === bgmTrack!.id ? { ...t, order: maxOrder + 1 } : t
                    ).sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
                    useTimelineStore.getState().setTracks(updatedTracks);
                    // bgmTrack 참조 업데이트 (아래에서 trackId 사용을 위해)
                    bgmTrack = updatedTracks.find(t => t.id === bgmTrack!.id)!;
                }
            }

            const audioItem: AudioItem = {
                id: crypto.randomUUID(),
                type: 'audio',
                trackId: bgmTrack.id,
                from: 0,
                durationInFrames: maxFrame,
                label: `BGM: ${randomBgm.fileName}`,
                src: randomBgm.id,
                mediaId: randomBgm.id,
                originId: crypto.randomUUID(),
                sourceStart: 0,
                sourceEnd: maxFrame,
                sourceDuration: maxFrame,
                sourceFps: fps,
                trimStart: 0,
                trimEnd: 0,
                volume: -20, // -30 dB는 너무 작아서 안 들릴 수 있으므로 -20 dB(약 10%)로 약간 조정
            };

            addItem(audioItem);
            alert(`✨ 랜덤 BGM "${randomBgm.fileName}"이 배치되었습니다!`);
        } catch (e: any) {
            console.error('[BGM AI] Error:', e);
            alert('BGM 배치 실패: ' + (e.message || ''));
        } finally {
            setIsLoading(false);
        }
    };

    const handleApplyBgm = async (bgm: any) => {
        try {
            const { tracks, items, fps, addItem, setTracks } = useTimelineStore.getState();

            if (items.length === 0) {
                alert('타임라인에 아이템이 없습니다.');
                return;
            }

            const maxFrame = items.reduce((max: number, item: TimelineItem) => Math.max(max, item.from + item.durationInFrames), 0);

            let bgmTrack = tracks.find((t: TimelineTrack) => t.name?.toLowerCase().includes('bgm'));
            const maxOrder = tracks.length > 0 ? Math.max(...tracks.map((t: TimelineTrack) => t.order ?? 0)) : 0;

            if (!bgmTrack) {
                bgmTrack = {
                    id: crypto.randomUUID(),
                    name: 'BGM',
                    height: 48,
                    locked: false,
                    visible: true,
                    muted: false,
                    solo: false,
                    order: maxOrder + 1,
                    items: [],
                };
                const updatedTracks = [...tracks, bgmTrack].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
                setTracks(updatedTracks);
            } else {
                const bgmItemsToRemove = items
                    .filter((item: TimelineItem) => item.trackId === bgmTrack!.id)
                    .map((item: TimelineItem) => item.id);
                if (bgmItemsToRemove.length > 0) {
                    useTimelineStore.getState().removeItems(bgmItemsToRemove);
                }
                if ((bgmTrack.order ?? 0) < maxOrder) {
                    const updatedTracks = tracks.map(t =>
                        t.id === bgmTrack!.id ? { ...t, order: maxOrder + 1 } : t
                    ).sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
                    setTracks(updatedTracks);
                    bgmTrack = updatedTracks.find(t => t.id === bgmTrack!.id)!;
                }
            }

            const audioItem: AudioItem = {
                id: crypto.randomUUID(),
                type: 'audio',
                trackId: bgmTrack.id,
                from: 0,
                durationInFrames: maxFrame,
                label: `BGM: ${bgm.fileName}`,
                src: bgm.id,
                mediaId: bgm.id,
                originId: crypto.randomUUID(),
                sourceStart: 0,
                sourceEnd: maxFrame,
                sourceDuration: maxFrame,
                sourceFps: fps,
                trimStart: 0,
                trimEnd: 0,
                volume: -20,
            };

            addItem(audioItem);
        } catch (e) {
            console.error('[BGM] Apply failed:', e);
        }
    };

    return (
        <div className="h-full flex flex-col bg-background">
            <div className="p-3 border-b border-border space-y-3">
                <button
                    onClick={handleRandomAssign}
                    disabled={isLoading}
                    className="w-full h-9 bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white text-[11px] font-bold rounded-lg transition-all flex items-center justify-center gap-1.5 disabled:opacity-50 px-1"
                >
                    {isLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                    {isLoading ? '배치 중...' : '랜덤 BGM 배치'}
                </button>

                <div className="relative">
                    <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                    <input
                        type="text"
                        placeholder="Search BGM..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full bg-secondary/30 border border-border rounded-md pl-8 pr-3 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-primary"
                    />
                </div>
            </div>

            <div className="flex-1 overflow-y-auto p-2 custom-scrollbar">
                <div className="grid grid-cols-1 gap-1">
                    {filteredBgms.map((bgm: any) => (
                        <div
                            key={bgm.id}
                            draggable
                            onDragStart={(e) => handleDragStart(e, bgm)}
                            onDragEnd={() => clearMediaDragData()}
                            className="group flex items-center gap-3 p-2 rounded-lg border border-transparent hover:border-primary/30 hover:bg-secondary/30 transition-all cursor-grab active:cursor-grabbing"
                        >
                            <div className="w-8 h-8 rounded bg-primary/10 flex items-center justify-center flex-shrink-0 group-hover:bg-primary/20 transition-colors">
                                <Music className="w-4 h-4 text-primary" />
                            </div>

                            <div className="flex-1 min-w-0">
                                <div className="text-[11px] font-medium text-foreground truncate">
                                    {bgm.fileName.replace(/\.[^/.]+$/, "").replace(/^\d+\./, "")}
                                </div>
                                <div className="text-[9px] text-muted-foreground">BGM • Preset</div>
                            </div>

                            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleApplyBgm(bgm);
                                    }}
                                    title="BGM 트랙에 적용"
                                    className="w-7 h-7 rounded-full bg-secondary flex items-center justify-center hover:bg-primary hover:text-primary-foreground transition-all"
                                >
                                    <Check className="w-3.5 h-3.5" />
                                </button>
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handlePlay(bgm.id);
                                    }}
                                    title={playingId === bgm.id ? '일시정지' : '미리듣기'}
                                    className="w-7 h-7 rounded-full bg-secondary flex items-center justify-center hover:bg-primary hover:text-primary-foreground transition-all"
                                >
                                    {playingId === bgm.id ? (
                                        <Pause className="w-3 h-3 fill-current" />
                                    ) : (
                                        <Play className="w-3 h-3 fill-current ml-0.5" />
                                    )}
                                </button>
                                {playingId === bgm.id && (
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            if (audioRef.current) {
                                                audioRef.current.pause();
                                                audioRef.current.currentTime = 0;
                                            }
                                            setPlayingId(null);
                                        }}
                                        title="정지"
                                        className="w-7 h-7 rounded-full bg-secondary flex items-center justify-center hover:bg-red-500 hover:text-white transition-all"
                                    >
                                        <Square className="w-3 h-3 fill-current" />
                                    </button>
                                )}
                            </div>
                        </div>
                    ))}

                    {filteredBgms.length === 0 && (
                        <div className="py-20 flex flex-col items-center justify-center text-center px-4 text-muted-foreground/50">
                            <Volume2 className="w-10 h-10 mb-2 opacity-20" />
                            <p className="text-xs">No BGM found</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
});
